import MapboxDraw from '@mapbox/mapbox-gl-draw'
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect'
import { useControl } from 'react-map-gl'
import { useCallback, useEffect } from 'react'

import { DrawingOption, Features } from 'features/MainRouter/features/Map/types'
import { DrawMode } from 'features/MainRouter/features/Map/hooks/useDrawingTool'

import { useEditorStyles } from './hooks'

interface Props {
  mapOptions: ConstructorParameters<typeof MapboxDraw>[0]
  mode: DrawMode
  features: Features
  drawingOption: DrawingOption
  onCreate: (evt: { features: Features }) => void
  onUpdate: (evt: { features: Features }) => void
  onDeleteFeature: (featureId: string) => void
}

export const Control: React.FC<Props> = ({
  mapOptions,
  onCreate,
  onUpdate,
  onDeleteFeature,
  mode,
  features,
  drawingOption,
}) => {
  const styles = useEditorStyles()

  const onSelectionChange = useCallback(
    ({ features: selectionFeatures }: { features: Features }) => {
      if (selectionFeatures?.length === 1 && selectionFeatures[0].id) {
        onDeleteFeature(selectionFeatures[0].id as string)
      }
    },
    [onDeleteFeature],
  )

  const control = useControl(
    ({ map }) => {
      map.on('draw.create', onCreate)
      map.on('draw.update', onUpdate)
      map.on('draw.selectionchange', onSelectionChange)
      return new MapboxDraw({ ...mapOptions, styles })
    },
    ({ map }) => {
      map.off('draw.create', onCreate)
      map.off('draw.update', onUpdate)
      map.off('draw.selectionchange', onSelectionChange)
    },
  )
  const drawControl = control as MapboxDraw

  useDeepCompareEffect(() => {
    // This is a workaraound to be able to delete a feature when selecting that option after
    // the feature is already selected
    if (drawingOption === 'delete') {
      drawControl.changeMode('simple_select')
    }

    if (drawingOption === 'hide') {
      drawControl.deleteAll()
      return
    }
    if (!features?.length) {
      drawControl.deleteAll()
      return
    }
    drawControl.set({
      type: 'FeatureCollection',
      features: features ?? [],
    })
  }, [features, drawingOption])

  useEffect(() => {
    switch (mode) {
      case 'simple_select': {
        drawControl.changeMode(mode)
        break
      }
      default: {
        drawControl.changeMode(mode)
      }
    }
  }, [drawControl, mode])

  return null
}
